import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "@/lib/i18n";
import { PANDO_TRIO_FLOW } from "@/lib/ecosystem-constants";
import {
  ChefHat,
  Sprout,
  Droplets,
  Sun,
  Home,
  type LucideIcon,
} from "lucide-react";
import sfnMark from "@/assets/ecosystem/sfn-mark.png.asset.json";
import plantyMark from "@/assets/ecosystem/plantypando.jpeg.asset.json";
import aquaMark from "@/assets/ecosystem/aquapando.jpeg.asset.json";
import solarMark from "@/assets/ecosystem/solarpando.png.asset.json";
import villageMark from "@/assets/ecosystem/villagepando.jpeg.asset.json";

const NODE_LOGO: Record<string, string> = {
  sfn: sfnMark.url,
  planty: plantyMark.url,
  aqua: aquaMark.url,
  solar: solarMark.url,
  village: villageMark.url,
};

/**
 * PANDO NEXUS COCKPIT
 * Interactive 5-node circular ecosystem wheel with animated cross-node
 * energy flow, neon theming per node, and a dynamic synergy description
 * panel. Fully responsive: wheel on top / description below on mobile,
 * side-by-side on lg+ screens.
 */

type NodeKey = "sfn" | "planty" | "aqua" | "solar" | "village";

type EcoNode = {
  key: NodeKey;
  label: string;
  short: string;
  tagline: string;
  icon: LucideIcon;
  /** Neon accent (OKLCH) */
  color: string;
  /** Softer glow for backgrounds */
  glow: string;
  /** Which sibling nodes this asset supplies value/energy to */
  flowsTo: NodeKey[];
  /** Dynamic synergy copy */
  synergy: {
    headline: string;
    body: string;
    bullets: { label: string; value: string }[];
  };
};

const NODES: EcoNode[] = [
  {
    key: "sfn",
    label: "SFN",
    short: "Sustainable Food & Eco Network",
    tagline: "Central Kitchen · Meal anchor",
    icon: ChefHat,
    color: "oklch(0.82 0.18 155)",
    glow: "oklch(0.82 0.18 155 / 0.35)",
    flowsTo: ["planty", "aqua", "village"],
    synergy: {
      headline: "SFN is the demand engine of the network.",
      body: "Every meal produced in the Central Kitchen consumes water, ingredients and energy at contracted internal rates — turning the other Pando assets into recurring, revenue-generating utilities instead of speculative infrastructure.",
      bullets: [
        { label: "Guaranteed offtake", value: "~1,200 meals/day at pilot capacity" },
        { label: "Internal contract rate", value: "−22% vs. local wholesale" },
        { label: "Feeds", value: "PlantyPando · AquaPando · VillagePando" },
      ],
    },
  },
  {
    key: "planty",
    label: "PlantyPando",
    short: "Land · Real estate · Renaturation",
    tagline: "Perennial ingredient supply",
    icon: Sprout,
    color: "oklch(0.84 0.16 160)",
    glow: "oklch(0.84 0.16 160 / 0.35)",
    flowsTo: ["sfn", "village"],
    synergy: {
      headline: "PlantyPando de-risks the food supply chain.",
      body: "Long-lease reforested land supplies SFN with tokenized ingredient forwards at fixed prices, insulating the Central Kitchen from spot-market volatility and simultaneously appreciating land value for VillagePando residents.",
      bullets: [
        { label: "Ingredient hedge", value: "Fixed 25y forward pricing" },
        { label: "Land appreciation", value: "Compounds under VillagePando" },
        { label: "Supplies", value: "SFN kitchens · Village residents" },
      ],
    },
  },
  {
    key: "aqua",
    label: "AquaPando",
    short: "Water supply · Reservoirs · Premium AquaP",
    tagline: "Filtered-water hubs",
    icon: Droplets,
    color: "oklch(0.8 0.14 230)",
    glow: "oklch(0.8 0.14 230 / 0.35)",
    flowsTo: ["sfn", "village", "planty"],
    synergy: {
      headline: "AquaPando guarantees production continuity.",
      body: "UV-filtered reservoirs remove dry-season risk for the Central Kitchen, irrigate PlantyPando parcels, and pipe premium AquaP water to Village residents — a single asset monetized three ways.",
      bullets: [
        { label: "Reservoir capacity", value: "20,000 L per hub" },
        { label: "Revenue streams", value: "3 (kitchen · irrigation · retail)" },
        { label: "Supplies", value: "SFN · Village · Planty" },
      ],
    },
  },
  {
    key: "solar",
    label: "SolarPando",
    short: "Solar parks · EV stations · Kitchen energy",
    tagline: "Zero-cost energy layer",
    icon: Sun,
    color: "oklch(0.86 0.16 90)",
    glow: "oklch(0.86 0.16 90 / 0.35)",
    flowsTo: ["sfn", "village", "aqua"],
    synergy: {
      headline: "SolarPando collapses the operating cost curve.",
      body: "Off-grid microgrids power the Central Kitchen, pump AquaPando reservoirs and light every VillagePando block at zero marginal energy cost — turning the largest OPEX line item across the network into a shared, tokenized asset.",
      bullets: [
        { label: "Peak generation", value: "12 kWp per anchor site" },
        { label: "OPEX offset", value: "≈100% of kitchen energy" },
        { label: "Powers", value: "SFN · Village · Aqua pumps" },
      ],
    },
  },
  {
    key: "village",
    label: "VillagePando",
    short: "Circular residential blocks",
    tagline: "Demand + governance layer",
    icon: Home,
    color: "oklch(0.9 0.02 260)",
    glow: "oklch(0.9 0.02 260 / 0.28)",
    flowsTo: ["sfn", "solar", "aqua", "planty"],
    synergy: {
      headline: "VillagePando closes the loop.",
      body: "Residential blocks are the end-consumer of every other asset — meals from SFN, energy from Solar, water from Aqua, land from Planty — routing recurring household spend back into the same tokens residents already hold as governance.",
      bullets: [
        { label: "Household demand", value: "Recurring, non-speculative" },
        { label: "Governance", value: "1 block = 1 vote weight" },
        { label: "Consumes", value: "All four upstream assets" },
      ],
    },
  },
];

const NODE_MAP = Object.fromEntries(NODES.map((n) => [n.key, n])) as Record<NodeKey, EcoNode>;

/** Wheel geometry — SVG viewBox is 100×100, nodes on a circle. */
const CENTER = 50;
const RADIUS = 38;

function polarPos(index: number, total: number) {
  // Start at top (-90°) and go clockwise
  const angle = (-90 + (360 / total) * index) * (Math.PI / 180);
  return {
    x: CENTER + RADIUS * Math.cos(angle),
    y: CENTER + RADIUS * Math.sin(angle),
  };
}

export function PandoNexusCockpit() {
  const { t } = useI18n();
  const [activeKey, setActiveKey] = useState<NodeKey>("sfn");
  const baseActive = NODE_MAP[activeKey];
  const localize = (n: EcoNode): EcoNode => ({
    ...n,
    tagline: t(`vp.nexus.${n.key}.tagline`),
    synergy: {
      headline: t(`vp.nexus.${n.key}.headline`),
      body: t(`vp.nexus.${n.key}.body`),
      bullets: [
        { label: t(`vp.nexus.${n.key}.b1.label`), value: t(`vp.nexus.${n.key}.b1.value`) },
        { label: t(`vp.nexus.${n.key}.b2.label`), value: t(`vp.nexus.${n.key}.b2.value`) },
        { label: t(`vp.nexus.${n.key}.b3.label`), value: t(`vp.nexus.${n.key}.b3.value`) },
      ],
    },
  });
  const active = localize(baseActive);

  const positions = useMemo(
    () => NODES.map((n, i) => ({ node: n, pos: polarPos(i, NODES.length) })),
    [],
  );
  const posMap = useMemo(
    () => Object.fromEntries(positions.map((p) => [p.node.key, p.pos])) as Record<
      NodeKey,
      { x: number; y: number }
    >,
    [positions],
  );

  return (
    <section
      aria-labelledby="pando-nexus-title"
      className="relative overflow-hidden rounded-3xl border border-white/10 bg-slate-900/70 p-6 backdrop-blur-xl sm:p-10"
    >
      {/* Ambient glows */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-70 transition-colors duration-700"
        style={{
          background: `radial-gradient(60% 45% at 50% 40%, ${active.glow}, transparent 65%), radial-gradient(50% 40% at 10% 100%, oklch(0.4 0.08 260 / 0.25), transparent 70%)`,
        }}
      />

      <header className="mb-8 flex flex-col items-start gap-2 sm:mb-10">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/50">
          {t("vp.nexus.eyebrow")}
        </span>
        <h2
          id="pando-nexus-title"
          className="font-display text-3xl font-medium tracking-tight text-white sm:text-4xl"
        >
          {t("vp.nexus.title")}
        </h2>
        <p className="max-w-2xl text-sm text-white/60">
          {t("vp.nexus.sub")}
        </p>
      </header>

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-center">
        {/* Wheel */}
        <div className="relative mx-auto aspect-square w-full max-w-[520px]">
          <svg
            viewBox="0 0 100 100"
            className="h-full w-full"
            role="img"
            aria-label={t("vp.nexus.wheelAriaLabel")}
          >
            <defs>
              <radialGradient id="nexus-core" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor={active.color} stopOpacity="0.55" />
                <stop offset="60%" stopColor={active.color} stopOpacity="0.08" />
                <stop offset="100%" stopColor="transparent" />
              </radialGradient>
              <filter id="nexus-glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="1.2" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Outer ring */}
            <circle
              cx={CENTER}
              cy={CENTER}
              r={RADIUS}
              fill="none"
              stroke="oklch(1 0 0 / 0.08)"
              strokeWidth="0.25"
              strokeDasharray="0.6 0.8"
            />
            <circle
              cx={CENTER}
              cy={CENTER}
              r={RADIUS - 8}
              fill="none"
              stroke="oklch(1 0 0 / 0.06)"
              strokeWidth="0.15"
            />

            {/* Static faint chords between all nodes (network mesh) */}
            {positions.map((a, i) =>
              positions.slice(i + 1).map((b) => (
                <line
                  key={`${a.node.key}-${b.node.key}`}
                  x1={a.pos.x}
                  y1={a.pos.y}
                  x2={b.pos.x}
                  y2={b.pos.y}
                  stroke="oklch(1 0 0 / 0.06)"
                  strokeWidth="0.15"
                />
              )),
            )}

            {/* Active flow lines from active node to its targets */}
            {active.flowsTo.map((target) => {
              const from = posMap[active.key];
              const to = posMap[target];
              const id = `flow-${active.key}-${target}`;
              return (
                <g key={id}>
                  <line
                    x1={from.x}
                    y1={from.y}
                    x2={to.x}
                    y2={to.y}
                    stroke={active.color}
                    strokeOpacity="0.35"
                    strokeWidth="0.35"
                    filter="url(#nexus-glow)"
                  />
                  {/* Pulsing packet */}
                  <motion.circle
                    r="0.9"
                    fill={active.color}
                    filter="url(#nexus-glow)"
                    initial={{ cx: from.x, cy: from.y, opacity: 0 }}
                    animate={{
                      cx: [from.x, to.x],
                      cy: [from.y, to.y],
                      opacity: [0, 1, 1, 0],
                    }}
                    transition={{
                      duration: 1.8,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                </g>
              );
            })}

            {/* Center core */}
            <circle cx={CENTER} cy={CENTER} r="14" fill="url(#nexus-core)" />
            <circle
              cx={CENTER}
              cy={CENTER}
              r="10"
              fill="oklch(0.14 0.01 260)"
              stroke={active.color}
              strokeOpacity="0.5"
              strokeWidth="0.3"
            />
            {/* Golden pulsing ring around center (Pando Nexus / Cockpit) */}
            <motion.circle
              cx={CENTER}
              cy={CENTER}
              r="12"
              fill="none"
              stroke="#f5c542"
              strokeOpacity="0.6"
              strokeWidth="0.35"
              animate={{ r: [11, 15, 11], strokeOpacity: [0.7, 0.15, 0.7] }}
              transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
            />
            <text
              x={CENTER}
              y={CENTER - 1}
              textAnchor="middle"
              fill="white"
              fontSize="2.2"
              fontFamily="ui-monospace, monospace"
              letterSpacing="0.15"
            >
              {t("vp.nexus.svgTitle")}
            </text>
            <text
              x={CENTER}
              y={CENTER + 2.6}
              textAnchor="middle"
              fill="oklch(1 0 0 / 0.6)"
              fontSize="1.4"
              fontFamily="ui-monospace, monospace"
              letterSpacing="0.2"
            >
              {t("vp.nexus.svgSubtitle")}
            </text>

            {/* Nodes */}
            {positions.map(({ node, pos }) => {
              const isActive = node.key === activeKey;
              return (
                <g
                  key={node.key}
                  transform={`translate(${pos.x} ${pos.y})`}
                  className="cursor-pointer"
                  onClick={() => setActiveKey(node.key)}
                  onMouseEnter={() => setActiveKey(node.key)}
                  role="button"
                  aria-label={`${t("vp.nexus.activateAria")} ${node.label}`}
                >
                  {isActive && (
                    <motion.circle
                      r="7"
                      fill={node.color}
                      opacity="0.18"
                      initial={{ scale: 0.6, opacity: 0 }}
                      animate={{ scale: [0.9, 1.2, 0.9], opacity: [0.25, 0.45, 0.25] }}
                      transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                    />
                  )}
                  <circle
                    r="5"
                    fill="oklch(0.13 0.01 260)"
                    stroke={node.color}
                    strokeOpacity={isActive ? 1 : 0.45}
                    strokeWidth={isActive ? "0.5" : "0.25"}
                    filter={isActive ? "url(#nexus-glow)" : undefined}
                  />
                </g>
              );
            })}
          </svg>

          {/* HTML overlay for logos + labels (crisper than SVG foreignObject) */}
          {positions.map(({ node, pos }) => {
            const isActive = node.key === activeKey;
            const logo = NODE_LOGO[node.key];
            return (
              <button
                key={node.key}
                type="button"
                onMouseEnter={() => setActiveKey(node.key)}
                onFocus={() => setActiveKey(node.key)}
                onClick={() => setActiveKey(node.key)}
                aria-pressed={isActive}
                aria-label={node.label}
                className="group absolute -translate-x-1/2 -translate-y-1/2 focus:outline-none"
                style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
              >
                <span
                  className="block h-12 w-12 overflow-hidden rounded-full border bg-slate-950/80 backdrop-blur transition-all duration-300 group-hover:scale-110 sm:h-16 sm:w-16"
                  style={{
                    borderColor: isActive ? node.color : "oklch(1 0 0 / 0.15)",
                    boxShadow: isActive
                      ? `0 0 22px ${node.glow}, 0 0 0 2px ${node.color} inset`
                      : "inset 0 0 0 1px rgba(255,255,255,0.06)",
                  }}
                >
                  <img
                    src={logo}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </span>
                <span
                  className="pointer-events-none absolute left-1/2 top-full mt-1.5 -translate-x-1/2 whitespace-nowrap font-mono text-[9px] uppercase tracking-[0.18em] transition-colors sm:text-[10px]"
                  style={{ color: isActive ? node.color : "oklch(1 0 0 / 0.55)" }}
                >
                  {node.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Synergy panel */}
        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.article
              key={active.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="rounded-2xl border bg-slate-950/60 p-6 backdrop-blur-xl sm:p-8"
              style={{
                borderColor: `color-mix(in oklab, ${active.color} 45%, transparent)`,
                boxShadow: `0 0 40px -12px ${active.glow}`,
              }}
            >
              <div className="flex items-center gap-3">
                <span
                  className="grid h-10 w-10 place-items-center rounded-full border bg-slate-900"
                  style={{ borderColor: active.color, boxShadow: `0 0 18px ${active.glow}` }}
                >
                  <active.icon className="h-5 w-5" style={{ color: active.color }} />
                </span>
                <div>
                  <div
                    className="font-mono text-[10px] uppercase tracking-[0.22em]"
                    style={{ color: active.color }}
                  >
                    {active.tagline}
                  </div>
                  <h3 className="font-display text-xl font-medium text-white sm:text-2xl">
                    {active.label}
                  </h3>
                </div>
              </div>

              <p
                className="mt-5 font-display text-lg leading-snug text-white sm:text-xl"
                style={{ textShadow: `0 0 24px ${active.glow}` }}
              >
                {active.synergy.headline}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-white/70">{active.synergy.body}</p>

              <dl className="mt-6 grid gap-3 sm:grid-cols-3">
                {active.synergy.bullets.map((b) => (
                  <div
                    key={b.label}
                    className="rounded-xl border border-white/10 bg-white/[0.03] p-3"
                  >
                    <dt className="font-mono text-[9px] uppercase tracking-[0.18em] text-white/50">
                      {b.label}
                    </dt>
                    <dd className="mt-1 text-sm text-white">{b.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-6 flex flex-wrap items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/50">
                  {t("vp.nexus.flowsIntoLabel")}
                </span>
                {active.flowsTo.map((k) => {
                  const n = NODE_MAP[k];
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setActiveKey(k)}
                      className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors hover:bg-white/5"
                      style={{
                        borderColor: `color-mix(in oklab, ${n.color} 45%, transparent)`,
                        color: n.color,
                      }}
                    >
                      <n.icon className="h-3 w-3" />
                      {n.label}
                    </button>
                  );
                })}
              </div>
            </motion.article>
          </AnimatePresence>
        </div>
      </div>

      {/* Pando-Trio directional flow caption + legend */}
      <div className="relative mt-10 overflow-hidden rounded-2xl border border-amber-300/25 bg-gradient-to-r from-emerald-500/[0.06] via-amber-400/[0.05] to-emerald-500/[0.06] p-5 sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-2xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-200/80">
              {t("vp.nexus.trio.eyebrow")}
            </div>
            <p
              className="mt-2 font-display text-xl font-medium leading-snug text-white sm:text-2xl"
              style={{ textShadow: "0 0 24px oklch(0.86 0.16 90 / 0.35)" }}
            >
              {t("vp.nexus.trio.caption")}
            </p>
          </div>
          <ol className="flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em] text-white/70">
            {PANDO_TRIO_FLOW.map((step, i) => (
              <li key={`${step.from}-${step.to}`} className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/40 px-2.5 py-1">
                  <motion.span
                    aria-hidden
                    className="h-1.5 w-1.5 rounded-full bg-amber-300"
                    animate={{ opacity: [0.35, 1, 0.35] }}
                    transition={{
                      duration: 1.6,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: i * 0.35,
                    }}
                  />
                  {t(`vp.nexus.trio.step${i + 1}`)}
                </span>
                {i < PANDO_TRIO_FLOW.length - 1 && (
                  <span aria-hidden className="text-amber-300/70">→</span>
                )}
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}


export default PandoNexusCockpit;
